'use client'

import { useState, useEffect, useMemo } from 'react'
import { useRouter } from 'next/navigation'
import { useTranslations } from 'next-intl'
import { useSprint, useSprintIssuesFromMultipleProjects } from '@/hooks/use-sprints'
import { useProjects, EnhancedProject } from '@/hooks/use-projects'
import type { DbSprint } from '@/lib/types/db-sprint'
import { statusToNumber, statusToString, DbSprintStatus } from '@/lib/types/db-sprint'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/hooks/use-toast'
import { EditSprintSidebar } from '@/components/sprints'
import { AddTaskToSprintModal } from '@/components/sprints/add-task-to-sprint-modal'
import { SprintCharts } from '@/components/sprints/sprint-charts'
import { CreateIssueSidebar } from '@/components/issues/create-issue-sidebar'
import ConfirmDialog from '@/components/ui/confirm-dialog'
import {
  ArrowLeft,
  Calendar,
  Target,
  Clock,
  PlayCircle,
  CheckCircle2,
  XCircle,
  BarChart3,
  TrendingUp,
  TrendingDown,
  Zap,
  Edit,
  LayoutGrid,
  Trash2,
  Activity,
  AlertCircle,
  ArrowUp,
  ArrowDown,
  Timer,
  ListTodo,
  ChevronRight,
  Plus,
  Filter,
  X as CloseIcon
} from 'lucide-react'
import { format, differenceInDays, parseISO } from 'date-fns'
import { cn } from '@/lib/utils'
import { IssueActionsModal } from '@/components/issues/issue-actions-modal'
import { IssuesTable } from '@/components/issues/issues-table'
import { IssueFilterSidebar, FilterState } from '@/components/issues/issue-filter-sidebar'
import { MoreVertical } from 'lucide-react'
import { getAuthHeaders } from '@/lib/api-utils'
import { SprintDetailsSkeleton } from './sprint-details-skeleton'

// Helper function to get all descendant (child) project IDs recursively
function getAllChildProjectIds(projects: EnhancedProject[], parentId: number): number[] {
  const childIds: number[] = []
  
  function traverse(projectList: EnhancedProject[]) {
    for (const project of projectList) {
      if (project.parent?.id === parentId || childIds.includes(project.parent?.id || -1)) {
        childIds.push(project.id)
      }
      if (project.children && project.children.length > 0) {
        traverse(project.children)
      }
    }
  }
  
  // Find the target project and its children
  function findAndTraverse(projectList: EnhancedProject[]) {
    for (const project of projectList) {
      if (project.id === parentId && project.children) {
        // Direct traversal of children
        const collectChildren = (children: EnhancedProject[]) => {
          for (const child of children) {
            childIds.push(child.id)
            if (child.children && child.children.length > 0) {
              collectChildren(child.children)
            }
          }
        }
        collectChildren(project.children)
        return
      }
      if (project.children && project.children.length > 0) {
        findAndTraverse(project.children)
      }
    }
  }
  
  findAndTraverse(projects)
  return childIds
}

interface SprintDetailsViewProps {
  projectId?: string
  sprintId: string
}

function getStatusIcon(status: string) {
  switch (status) {
    case 'open':
      return <Clock className="h-4 w-4" />
    case 'active':
      return <PlayCircle className="h-4 w-4" />
    case 'closed':
      return <CheckCircle2 className="h-4 w-4" />
    default:
      return <Clock className="h-4 w-4" />
  }
}

function getStatusBadge(status: string) {
  const badgeConfig: Record<string, string> = {
    active: 'border-green-600 text-green-700 dark:border-green-500 dark:text-green-400',
    open: 'border-gray-500 text-gray-700 dark:border-gray-400 dark:text-gray-300',
    closed: 'border-blue-600 text-blue-700 dark:border-blue-500 dark:text-blue-400'
  }

  const className = badgeConfig[status] || badgeConfig.open

  return (
    <Badge variant="outline" className={className}>
      <span className="capitalize">{status}</span>
    </Badge>
  )
}

// Helper function to parse ISO date strings
function parseDate(dateString: string | null): Date | null {
  if (!dateString) return null
  return new Date(dateString)
}

export function SprintDetailsView({ projectId, sprintId }: SprintDetailsViewProps) {
  const t = useTranslations('sprintsPage')
  const router = useRouter()
  const { toast } = useToast()
  
  // Load projects to get the child project hierarchy
  const { data: projectsData, isLoading: projectsLoading } = useProjects()

  // Use useSprint to get single sprint directly (projectId is optional now)
  const { sprint, loading: sprintLoading, refetch } = useSprint(null, sprintId)

  // Derive effective project ID from props or sprint data
  const effectiveProjectId = useMemo(() => {
    return projectId ? parseInt(projectId) : sprint?.projectId
  }, [projectId, sprint?.projectId])

  // Calculate all project IDs (current + all children) for fetching sprint issues
  const allProjectIds = useMemo(() => {
    if (!effectiveProjectId || !projectsData?.projects) return effectiveProjectId ? [effectiveProjectId] : []

    const childIds = getAllChildProjectIds(projectsData.projects, effectiveProjectId)
    return [effectiveProjectId, ...childIds]
  }, [effectiveProjectId, projectsData?.projects])
  // Use the multi-project hook to get issues from current project AND all child projects
  const { issues: mappings, loading: issuesLoading, refetch: refetchMappings } = useSprintIssuesFromMultipleProjects(
    allProjectIds,
    sprintId
  )
  const [sprintIssues, setSprintIssues] = useState<any[]>([])
  const [fetchingIssues, setFetchingIssues] = useState(true) // Start as true to show skeleton initially
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false)
  const [showEditSidebar, setShowEditSidebar] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [showIssueActions, setShowIssueActions] = useState(false)
  const [selectedIssue, setSelectedIssue] = useState<any | null>(null)
  const [failedIssueIds, setFailedIssueIds] = useState<number[]>([])
  const [showAddTaskModal, setShowAddTaskModal] = useState(false)
  const [showCreateIssueSidebar, setShowCreateIssueSidebar] = useState(false)

  // Filter state - using FilterState interface from IssueFilterSidebar
  const [filters, setFilters] = useState<FilterState>({
    status: [],
    tracker: [],
    priority: [],
    assignee: [],
    version: [],
    author: [],
    category: [],
    parent: []
  })
  const [showFilters, setShowFilters] = useState(false)

  const handleOpenIssueActions = (issue: any, e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setSelectedIssue(issue)
    setShowIssueActions(true)
  }

  const handleCloseIssueActions = () => {
    setShowIssueActions(false)
    setSelectedIssue(null)
  }

  const handleIssueActionComplete = async (updatedIssue?: any) => {
    try {
      // Clear any previous failed issue IDs
      setFailedIssueIds([])

      // Optimistic update: immediately update the local state if we have updated issue data
      if (updatedIssue && selectedIssue) {
        console.log('[SprintDetailsView] Applying optimistic update for issue:', updatedIssue.id)
        setSprintIssues(prevIssues => 
          prevIssues.map(issue => 
            issue.id === updatedIssue.id 
              ? { ...issue, ...updatedIssue }
              : issue
          )
        )
      }

      // Close the modal immediately for better UX
      handleCloseIssueActions()

      // Refetch in the background to ensure consistency
      // This will update with any changes made by other users or backend processing
      await refetchMappings()

    } catch (error) {
      console.error('[SprintDetailsView] Error refetching mappings:', error)
      toast({
        title: 'Error',
        description: 'Failed to refresh task list. Please reload the page.',
        variant: 'destructive'
      })
      // Still close the modal even on error
      handleCloseIssueActions()
    }
  }


  // Update sprint issues when mappings (now full issue objects) change
  useEffect(() => {
    console.log('[SprintDetails] Mappings changed:', {
      count: mappings?.length || 0,
      issueIds: mappings?.map((i: any) => i.id) || [],
      contains16893: mappings?.some((i: any) => i.id === 16893)
    })

    if (!mappings || mappings.length === 0) {
      // No issues, clear sprint issues and failed IDs
      setSprintIssues([])
      setFailedIssueIds([])
      setFetchingIssues(false)
      return
    }

    // Mappings now contain full issue objects from the database
    // Filter out issues with missing or invalid subjects (corrupt data)
    const validIssues = mappings.filter((issue: any) => {
      // Exclude issues that have no subject or subject is just numbers (likely showing parent_id)
      const hasValidSubject = issue.subject && issue.subject.trim() && !/^\d+$/.test(issue.subject.trim())
      if (!hasValidSubject) {
        console.warn('[SprintDetails] Filtering out invalid issue:', {
          id: issue.id,
          subject: issue.subject,
          parent_id: issue.parent_id
        })
      }
      return hasValidSubject
    })

    // No need to fetch from Redmine API - just use them directly
    setSprintIssues(validIssues)
    setFailedIssueIds([])
    setFetchingIssues(false)
    console.log('[SprintDetails] Updated sprintIssues state with', validIssues.length, 'valid issues (filtered', mappings.length - validIssues.length, 'invalid)')
  }, [mappings])

  const loading = sprintLoading || issuesLoading || fetchingIssues

  // Handle status change
  const handleStatusChange = async (newStatus: string) => {
    if (!sprint) return

    setIsUpdatingStatus(true)
    try {
      const res = await fetch(`/api/db/sprints/${sprint.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: statusToNumber(newStatus) })
      })

      if (!res.ok) {
        throw new Error('Failed to update sprint status')
      }

      await refetch()

      toast({
        title: 'Success',
        description: `Sprint status updated to ${newStatus}`,
        variant: 'default'
      })
    } catch (error) {
      console.error('Error updating sprint status:', error)
      toast({
        title: 'Error',
        description: 'Failed to update sprint status',
        variant: 'destructive'
      })
    } finally {
      setIsUpdatingStatus(false)
    }
  }

  const handleDeleteSprint = async () => {
    if (!sprint) return

    setIsDeleting(true)
    try {
      const res = await fetch(`/api/db/sprints/${sprint.id}`, {
        method: 'DELETE'
      })

      if (!res.ok) {
        throw new Error('Failed to delete sprint')
      }

      toast({
        title: 'Success',
        description: 'Sprint deleted successfully',
        variant: 'default'
      })

      // Notify sidebar to refetch sprints
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('sprint-changed'))
      }

      // Navigate back to appropriate sprint list
      if (projectId) {
        router.push(`/projects/${projectId}/sprints`)
      } else {
        router.push('/sprints')
      }
    } catch (error) {
      console.error('Error deleting sprint:', error)
      toast({
        title: 'Error',
        description: 'Failed to delete sprint',
        variant: 'destructive'
      })
    } finally {
      setIsDeleting(false)
    }
  }

  // Calculate sprint statistics
  // Use sprintIssues.length to only count successfully fetched issues
  // This ensures consistency with completedIssues calculation
  const totalIssues = sprintIssues.length

  // Parse ISO date strings to Date objects
  const startDate = parseDate(sprint?.startDate || null)
  const endDate = parseDate(sprint?.endDate || null)

  // Calculate sprint timeline
  const daysRemaining = endDate 
    ? Math.max(0, differenceInDays(endDate, new Date()))
    : 0
  const totalDays = startDate && endDate
    ? differenceInDays(endDate, startDate)
    : 0
  const daysPassed = Math.max(0, totalDays - daysRemaining)

  // Get unique filter options in IssueFilterSidebar format
  const filterOptions = useMemo(() => {
    const statuses = new Map<number, { id: number; name: string; is_closed?: boolean }>()
    const trackers = new Map<number, { id: number; name: string }>()
    const priorities = new Map<number, { id: number; name: string }>()
    const assignees = new Map<number, { id: number; name: string }>()

    sprintIssues.forEach(issue => {
      if (issue.status) {
        statuses.set(issue.status.id, {
          id: issue.status.id,
          name: issue.status.name,
          is_closed: issue.status.is_closed
        })
      }
      if (issue.tracker) {
        trackers.set(issue.tracker.id, {
          id: issue.tracker.id,
          name: issue.tracker.name
        })
      }
      if (issue.priority) {
        priorities.set(issue.priority.id, {
          id: issue.priority.id,
          name: issue.priority.name
        })
      }
      if (issue.assigned_to) {
        assignees.set(issue.assigned_to.id, {
          id: issue.assigned_to.id,
          name: issue.assigned_to.name
        })
      }
    })

    return {
      statuses: Array.from(statuses.values()).sort((a, b) => a.name.localeCompare(b.name)),
      trackers: Array.from(trackers.values()).sort((a, b) => a.name.localeCompare(b.name)),
      priorities: Array.from(priorities.values()).sort((a, b) => a.name.localeCompare(b.name)),
      assignees: Array.from(assignees.values()).sort((a, b) => a.name.localeCompare(b.name))
    }
  }, [sprintIssues])

  // Apply filters
  const filteredIssues = useMemo(() => {
    const filtered = sprintIssues.filter(issue => {
      // Status filter
      if (filters.status.length > 0 && !filters.status.includes(String(issue.status?.id || ''))) {
        return false
      }
      // Tracker filter
      if (filters.tracker.length > 0 && !filters.tracker.includes(String(issue.tracker?.id || ''))) {
        return false
      }
      // Priority filter
      if (filters.priority.length > 0 && !filters.priority.includes(String(issue.priority?.id || ''))) {
        return false
      }
      // Assignee filter
      if (filters.assignee.length > 0 && !filters.assignee.includes(String(issue.assigned_to?.id || ''))) {
        return false
      }
      return true
    })
    console.log('[SprintDetails] Filtered issues:', {
      totalIssues: sprintIssues.length,
      filteredCount: filtered.length,
      contains16893: filtered.some(i => i.id === 16893),
      hasFilters: Object.values(filters).some(arr => arr.length > 0),
      filters
    })
    return filtered
  }, [sprintIssues, filters])

  const hasActiveFilters = Object.values(filters).some(arr => arr.length > 0)

  // Calculate actual completed issues based on issue status (is_closed flag)
  const completedIssues = sprintIssues.filter(issue => {
    const isClosed = issue.status?.is_closed === true || issue.status?.is_closed === 1
    return isClosed
  }).length
  
  const progress = totalIssues > 0 ? Math.round((completedIssues / totalIssues) * 100) : 0

  if (loading) {
    return <SprintDetailsSkeleton />
  }

  if (!sprint) {
    return (
      <div className="w-full px-4 sm:px-6 lg:px-8 py-6">
        <Card>
          <CardContent className="p-12 text-center">
            <Target className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Sprint Not Found</h3>
            <p className="text-muted-foreground mb-2">
              The sprint you're looking for doesn't exist.
            </p>
            <Button onClick={() => {
              if (projectId) {
                router.push(`/projects/${projectId}/sprints`)
              } else {
                router.push('/sprints')
              }
            }}>
              Back to Sprints
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Convert numeric status to string for comparisons
  const sprintStatus = statusToString(sprint.status)

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 py-6">
      {/* Compact Header - Max 3 Lines */}
      <div className="mb-6 space-y-2">
        {/* Line 1: Back Button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            if (projectId) {
              router.push(`/projects/${projectId}/sprints`)
            } else {
              router.push('/sprints')
            }
          }}
          className="gap-2 -ml-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Sprints
        </Button>

        {/* Line 2: Title (Left) + Status & Buttons (Right) */}
        <div className="flex items-center justify-between gap-4">
          {/* Left: Title */}
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-bold">{sprint.name}</h1>
          </div>

          {/* Right: Status Dropdown & Action Buttons */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <Select
              value={sprintStatus}
              onValueChange={handleStatusChange}
              disabled={isUpdatingStatus}
            >
              <SelectTrigger className="w-[300px] h-10 rounded-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="open">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Open
                  </div>
                </SelectItem>
                <SelectItem value="active">
                  <div className="flex items-center gap-2">
                    <PlayCircle className="h-4 w-4" />
                    Active
                  </div>
                </SelectItem>
                <SelectItem value="closed">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4" />
                    Closed
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>

            <Button
              variant="outline"
              size="sm"
              onClick={() => router.push(`/projects/${effectiveProjectId}/sprints/${sprintId}/kanban`)}
              className="h-10 rounded-full"
            >
              <LayoutGrid className="h-3.5 w-3.5 mr-1.5" />
              Kanban Board
            </Button>

            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setShowAddTaskModal(true)}
              className="h-10 rounded-full"
            >
              <Plus className="h-3.5 w-3.5 mr-1.5" />
              Add Tasks
            </Button>

            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setShowEditSidebar(true)}
              className="h-10 rounded-full"
            >
              <Edit className="h-3.5 w-3.5 mr-1.5" />
              Edit Sprint
            </Button>
          </div>
        </div>

        {/* Line 3: Date & Days Remaining */}
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          {sprint.startDate && (
            <span>
              {startDate && format(startDate, 'MMM d, yyyy')}
              {endDate && ` - ${format(endDate, 'MMM d, yyyy')}`}
            </span>
          )}

          {sprintStatus === 'active' && daysRemaining >= 0 && (
            <span>
              • {daysRemaining} day{daysRemaining !== 1 ? 's' : ''} remaining
            </span>
          )}
        </div>
      </div>

      {/* Failed Issues Warning Banner */}
      {failedIssueIds.length > 0 && (
        <div className="mb-6">
          <Card className="border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-950/20">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-orange-600 dark:text-orange-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-orange-900 dark:text-orange-200">
                    Unable to load {failedIssueIds.length} issue{failedIssueIds.length !== 1 ? 's' : ''}
                  </p>
                  <p className="text-xs text-orange-700 dark:text-orange-300 mt-1">
                    The following issues could not be fetched from the server: #{failedIssueIds.join(', #')}.
                    This may be due to server connectivity issues or permissions. The sprint view is showing available issues only.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Statistics Cards */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4 mb-6">
        {/* Total Issues */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('totalTasks')}</CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalIssues}</div>
            <p className="text-xs text-muted-foreground">
              {t('tasksInThisSprint')}
            </p>
          </CardContent>
        </Card>

        {/* Progress */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('progress')}</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{progress}%</div>
            <p className="text-xs text-muted-foreground">
              {t('ofTasks', { completed: completedIssues, total: totalIssues })}
            </p>
          </CardContent>
        </Card>

        {/* Story Points */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('storyPoints')}</CardTitle>
            <Zap className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {sprintIssues.reduce((sum, issue) => sum + (issue.storyPoints || 0), 0)}
            </div>
            <p className="text-xs text-muted-foreground">
              {t('totalStoryPoints')}
            </p>
          </CardContent>
        </Card>

        {/* Timeline */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('timeline')}</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {daysPassed}/{totalDays}
            </div>
            <p className="text-xs text-muted-foreground">
              {t('daysElapsed')}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* AI Summary Section */}
      <Card className="mb-6">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Activity className="h-5 w-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-base md:text-lg">{t('aiSprintAnalysis')}</CardTitle>
                <CardDescription className="text-xs mt-1">
                  {t('intelligentInsights')}
                </CardDescription>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* AI Generated Summary */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 mb-3">
              <div className="h-px flex-1 bg-border"></div>
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('summary')}</span>
              <div className="h-px flex-1 bg-border"></div>
            </div>
            <p className="text-sm leading-relaxed">
              {sprintStatus === 'active' && daysRemaining > 0 ? (
                <>
                  {progress === 0 && daysPassed === 0 ? (
                    <>Sprint has just started with <strong>{totalIssues} tasks</strong> planned. The team has <strong>{daysRemaining} days</strong> to complete the work. Focus on establishing a steady workflow and completing initial high-priority tasks.</>
                  ) : progress < 25 ? (
                    <>Sprint progress is at <strong>{progress}%</strong> with <strong>{daysRemaining} days remaining</strong>. Current velocity shows <strong>{daysPassed > 0 ? (completedIssues / daysPassed).toFixed(1) : '0'} tasks/day</strong>. The team needs to accelerate pace to meet sprint goals - consider identifying and removing blockers.</>
                  ) : progress < 50 ? (
                    <>Sprint is progressing steadily at <strong>{progress}% completion</strong>. With <strong>{completedIssues} tasks done</strong> and <strong>{totalIssues - completedIssues} remaining</strong>, the team is building momentum. Current velocity of <strong>{daysPassed > 0 ? (completedIssues / daysPassed).toFixed(1) : '0'} tasks/day</strong> suggests good progress trajectory.</>
                  ) : progress < 75 ? (
                    <>Strong progress with <strong>{progress}% complete</strong>. The team has finished <strong>{completedIssues} of {totalIssues} tasks</strong>. With <strong>{daysRemaining} days left</strong>, sprint goals are within reach. Maintain current velocity and focus on quality delivery.</>
                  ) : progress < 100 ? (
                    <>Excellent progress at <strong>{progress}% completion</strong>! Only <strong>{totalIssues - completedIssues} tasks remaining</strong> with <strong>{daysRemaining} days</strong> to go. The team is on track for successful sprint completion. Focus on wrapping up final items and sprint retrospective planning.</>
                  ) : (
                    <>Sprint goals achieved! All <strong>{totalIssues} tasks completed</strong> with <strong>{daysRemaining} days remaining</strong>. Outstanding performance by the team. Consider pulling in additional work from backlog or focusing on technical debt.</>
                  )}
                </>
              ) : sprintStatus === 'closed' ? (
                <>Sprint concluded successfully with <strong>{completedIssues} of {totalIssues} tasks</strong> completed ({progress}%). {progress >= 80 ? 'Excellent execution and team performance.' : progress >= 60 ? 'Good delivery with room for improvement in upcoming sprints.' : 'Consider reviewing sprint planning and estimation processes for better outcomes.'}</>
              ) : sprintStatus === 'open' ? (
                <>Sprint is scheduled with <strong>{totalIssues} tasks</strong> ready for execution. Duration spans <strong>{totalDays} days</strong> from {startDate ? format(startDate, 'MMM d') : ''} to {endDate ? format(endDate, 'MMM d') : ''}. Ensure team capacity alignment and sprint goal clarity before kickoff.</>
              ) : (
                <>Sprint has <strong>{completedIssues} tasks</strong> completed out of <strong>{totalIssues}</strong> planned. Current status: {sprintStatus}.</>
              )}
            </p>
          </div>

          {/* Metrics Grid */}
          {sprintStatus === 'active' && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">{t('velocity')}</p>
                <p className="text-xl font-bold">
                  {daysPassed > 0 ? (completedIssues / daysPassed).toFixed(1) : '0.0'}
                  <span className="text-xs font-normal text-muted-foreground ml-1">/day</span>
                </p>
              </div>
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">{t('projected')}</p>
                <p className="text-xl font-bold">
                  {Math.round(progress + (daysPassed > 0 ? (completedIssues / daysPassed) * daysRemaining / totalIssues * 100 : 0))}%
                </p>
              </div>
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">{t('completed')}</p>
                <p className="text-xl font-bold">{completedIssues}<span className="text-xs font-normal text-muted-foreground ml-1">/ {totalIssues}</span></p>
              </div>
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">{t('daysLeft', { days: daysRemaining })}</p>
                <p className="text-xl font-bold">{daysRemaining}</p>
              </div>
            </div>
          )}

          {/* Action Items */}
          {sprintStatus === 'active' && (totalIssues - completedIssues) > 0 && (() => {
            const hasBehindSchedule = progress < 30 && daysRemaining < totalDays / 3
            const hasAlmostDone = totalIssues - completedIssues <= 5 && daysRemaining > 2
            const hasAheadSchedule = progress >= 80 && daysRemaining > totalDays / 4
            const hasBelowVelocity = daysPassed > 0 && (completedIssues / daysPassed) < (totalIssues / totalDays * 0.8) && sprintStatus === 'active'
            const hasAnyActionItem = hasBehindSchedule || hasAlmostDone || hasAheadSchedule || hasBelowVelocity

            if (!hasAnyActionItem) return null

            return (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <div className="h-px flex-1 bg-border"></div>
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Action Items</span>
                  <div className="h-px flex-1 bg-border"></div>
                </div>
                <div className="space-y-2">
                  {hasBehindSchedule && (
                    <div className="flex items-start gap-3 text-sm">
                      <div className="p-1.5 bg-orange-500/10 rounded-md mt-0.5">
                        <AlertCircle className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">Prioritize Critical Tasks</p>
                        <p className="text-xs text-muted-foreground mt-0.5">Sprint is behind schedule. Focus on high-impact items and remove blockers.</p>
                      </div>
                    </div>
                  )}
                  {hasAlmostDone && (
                    <div className="flex items-start gap-3 text-sm">
                      <div className="p-1.5 bg-primary/10 rounded-md mt-0.5">
                        <CheckCircle2 className="h-4 w-4 text-primary" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">Wrap Up Remaining Items</p>
                        <p className="text-xs text-muted-foreground mt-0.5">Only {totalIssues - completedIssues} tasks left. Focus on quality completion and documentation.</p>
                      </div>
                    </div>
                  )}
                  {hasAheadSchedule && (
                    <div className="flex items-start gap-3 text-sm">
                      <div className="p-1.5 bg-primary/10 rounded-md mt-0.5">
                        <TrendingUp className="h-4 w-4 text-primary" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">Consider Additional Work</p>
                        <p className="text-xs text-muted-foreground mt-0.5">Ahead of schedule. Evaluate pulling in additional backlog items.</p>
                      </div>
                    </div>
                  )}
                  {hasBelowVelocity && (
                    <div className="flex items-start gap-3 text-sm">
                      <div className="p-1.5 bg-orange-500/10 rounded-md mt-0.5">
                        <Activity className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">{t('velocityBelowTarget')}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">Team velocity is lower than planned. Review capacity and identify bottlenecks.</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )
          })()}
        </CardContent>
      </Card>

      {/* Sprint Charts Section */}
      <div className="mb-6">
        <SprintCharts sprintId={sprintId} projectId={effectiveProjectId} />
      </div>

      {/* Sprint Velocity & Progress Section */}
      <div className="grid gap-6 md:grid-cols-2 mb-6">
        {/* Sprint Progress Overview */}
        <Card className="flex flex-col">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base md:text-lg">
              <Target className="h-5 w-5" />
              {t('sprintProgress')}
            </CardTitle>
            <CardDescription className="text-sm">
              {t('overallSprintStatus')}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col flex-1 space-y-6">
            {/* Large circular progress */}
            <div className="flex items-center justify-center py-4">
              <div className="relative w-40 h-40">
                <svg className="transform -rotate-90 w-40 h-40">
                  <circle
                    cx="80"
                    cy="80"
                    r="70"
                    stroke="currentColor"
                    strokeWidth="12"
                    fill="transparent"
                    className="text-muted"
                  />
                  <circle
                    cx="80"
                    cy="80"
                    r="70"
                    stroke="currentColor"
                    strokeWidth="12"
                    fill="transparent"
                    strokeDasharray={`${2 * Math.PI * 70}`}
                    strokeDashoffset={`${2 * Math.PI * 70 * (1 - progress / 100)}`}
                    className="text-primary transition-all duration-500"
                    strokeLinecap="round"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-4xl font-bold">{progress}%</div>
                    <div className="text-sm text-muted-foreground">{t('complete')}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Progress breakdown */}
            <div className="space-y-3 mt-auto">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-3 rounded-lg border">
                  <div className="text-2xl font-bold text-primary">{completedIssues}</div>
                  <div className="text-xs text-muted-foreground mt-1">{t('completed')}</div>
                </div>
                <div className="text-center p-3 rounded-lg border">
                  <div className="text-2xl font-bold">{totalIssues - completedIssues}</div>
                  <div className="text-xs text-muted-foreground mt-1">{t('remaining')}</div>
                </div>
              </div>

              {/* Timeline info */}
              <div className="pt-3 border-t space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{t('daysPassed')}</span>
                  <span className="font-semibold">{daysPassed} / {totalDays}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{t('daysRemaining')}</span>
                  <span className="font-semibold">{daysRemaining}</span>
                </div>
                {daysPassed > 0 && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{t('dailyVelocity')}</span>
                    <span className="font-semibold">{(completedIssues / daysPassed).toFixed(1)} tasks/day</span>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Sprint Velocity */}
        <Card className="flex flex-col">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base md:text-lg">
              <Zap className="h-5 w-5" />
              {t('teamVelocity')}
            </CardTitle>
            <CardDescription className="text-sm">
              {t('taskCompletionRate')}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col flex-1 space-y-6">
            {/* Velocity metrics display */}
            <div className="flex flex-col items-center justify-center py-4">
              <div className="text-center mb-6">
                <div className="text-5xl font-bold text-primary">
                  {daysPassed > 0 ? (completedIssues / daysPassed).toFixed(1) : '0.0'}
                </div>
                <div className="text-sm text-muted-foreground mt-2">{t('tasksPerDay')}</div>
              </div>
              
              {/* Progress bar showing sprint timeline */}
              <div className="w-full space-y-2">
                <div className="flex justify-between text-xs text-muted-foreground mb-1">
                  <span>Day 1</span>
                  <span>Day {totalDays}</span>
                </div>
                <div className="h-3 bg-muted rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-primary rounded-full transition-all duration-500"
                    style={{ width: `${totalDays > 0 ? (daysPassed / totalDays) * 100 : 0}%` }}
                  />
                </div>
                <div className="text-center text-xs text-muted-foreground mt-1">
                  {daysPassed} of {totalDays} {t('daysElapsed')}
                </div>
              </div>
            </div>

            {/* Velocity stats */}
            <div className="space-y-3 mt-auto">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-3 rounded-lg border">
                  <div className="text-2xl font-bold text-primary">
                    {totalDays > 0 && daysPassed > 0 && completedIssues > 0
                      ? Math.ceil((totalIssues - completedIssues) / (completedIssues / daysPassed))
                      : '—'}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">{t('daysToComplete')}</div>
                </div>
                <div className="text-center p-3 rounded-lg border">
                  <div className="text-2xl font-bold">
                    {daysPassed > 0 && daysRemaining > 0
                      ? Math.round((completedIssues / daysPassed) * daysRemaining)
                      : '—'}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">{t('projectedTasks')}</div>
                </div>
              </div>

              {/* Performance indicators */}
              <div className="pt-3 border-t space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{t('targetVelocity')}</span>
                  <span className="font-semibold">
                    {totalDays > 0 ? (totalIssues / totalDays).toFixed(1) : '0.0'} tasks/day
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{t('currentVelocity')}</span>
                  <span className="font-semibold">
                    {daysPassed > 0 ? (completedIssues / daysPassed).toFixed(1) : '0.0'} tasks/day
                  </span>
                </div>
                {daysPassed > 0 && totalDays > 0 && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{t('performance')}</span>
                    <span className={`font-semibold ${
                      (completedIssues / daysPassed) >= (totalIssues / totalDays)
                        ? 'text-primary'
                        : 'text-orange-500'
                    }`}>
                      {((completedIssues / daysPassed) / (totalIssues / totalDays) * 100).toFixed(0)}%
                    </span>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Sprint Tasks Widget - Using shared IssuesTable component */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-primary" />
              {t('sprintTasks')}
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button
                variant={hasActiveFilters ? "default" : "outline"}
                size="sm"
                onClick={() => setShowFilters(true)}
              >
                <Filter className="h-4 w-4 mr-1" />
                {t('filters.filters')}
                {hasActiveFilters && (
                  <Badge variant="secondary" className="ml-1.5 bg-background/80">
                    {Object.values(filters).flat().length}
                  </Badge>
                )}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowAddTaskModal(true)}
              >
                <Plus className="h-4 w-4 mr-1" />
                {t('tasks.addTask')}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => router.push(`/projects/${effectiveProjectId}/sprints/${sprintId}/kanban`)}
              >
                {t('viewKanban')}
                <ChevronRight className="h-3 w-3 ms-1" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0 pt-0">
          {sprintIssues.length === 0 && !loading ? (
            <div className="text-center py-8 border-t border-border">
              <ListTodo className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground mb-3">
                {mappings.length === 0
                  ? 'No tasks assigned to this sprint yet'
                  : 'Loading task details...'}
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => router.push(`/projects/${effectiveProjectId}/sprints/${sprintId}/kanban`)}
              >
                <Plus className="h-4 w-4 mr-2" />
                {t('addTasksInKanban')}
              </Button>
            </div>
          ) : filteredIssues.length === 0 && hasActiveFilters ? (
            <div className="text-center py-8 border-t border-border">
              <Filter className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground mb-3">
                No tasks match the selected filters
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setFilters({
                  status: [],
                  tracker: [],
                  priority: [],
                  assignee: [],
                  version: [],
                  author: [],
                  category: [],
                  parent: []
                })}
              >
                {t('filters.clearFilters')}
              </Button>
            </div>
          ) : (
            <IssuesTable
              issues={filteredIssues}
              loading={loading}
              showStoryPoints={true}
              currentProjectId={projectId ? parseInt(projectId) : undefined}
              onOpenMenu={(issue) => {
                setSelectedIssue(issue as any)
                setShowIssueActions(true)
              }}
              emptyMessage={t('tasks.noTasksAssigned')}
            />
          )}
        </CardContent>
      </Card>

      {/* Edit Sprint Sidebar */}
      <EditSprintSidebar
        isOpen={showEditSidebar}
        onClose={() => setShowEditSidebar(false)}
        sprint={sprint}
        projectId={effectiveProjectId ?? null}
        onSuccess={() => {
          // Refetch sprint data
          setShowEditSidebar(false)
          // The sprint data will auto-refresh through the useSprints hook
        }}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteSprint}
        title={t('deleteSprint')}
        description={t('deleteConfirmation')}
        confirmText={t('delete')}
        cancelText={t('cancel')}
        variant="danger"
      />

      {/* Issue Actions Modal */}
      {selectedIssue && effectiveProjectId && (
        <IssueActionsModal
          issue={selectedIssue}
          isOpen={showIssueActions}
          onClose={handleCloseIssueActions}
          onActionComplete={handleIssueActionComplete}
          projectId={effectiveProjectId}
          currentSprintId={parseInt(sprintId)}
        />
      )}

      {/* Add Task to Sprint Modal */}
      {effectiveProjectId && (
        <AddTaskToSprintModal
          isOpen={showAddTaskModal}
          onClose={() => setShowAddTaskModal(false)}
          projectId={effectiveProjectId}
          sprintId={sprintId}
          onSuccess={() => {
            setShowAddTaskModal(false)
            // Refetch sprint issues to update the task list
            refetchMappings()
          }}
          onCreateNewTask={() => {
            // Close add task modal and open create issue sidebar
            setShowAddTaskModal(false)
            setShowCreateIssueSidebar(true)
        }}
        />
      )}

      {/* Create Issue Sidebar */}
      <CreateIssueSidebar
        isOpen={showCreateIssueSidebar}
        onClose={() => setShowCreateIssueSidebar(false)}
        onSuccess={() => {
          setShowCreateIssueSidebar(false)
          // Refetch sprint issues to show the newly created task
          refetchMappings()
          toast({
            title: 'Success',
            description: 'Task created successfully',
            variant: 'default'
          })
        }}
      />

      {/* Filter Sidebar */}
      <IssueFilterSidebar
        isOpen={showFilters}
        onClose={() => setShowFilters(false)}
        filters={filters}
        onFiltersChange={setFilters}
        trackers={filterOptions.trackers}
        statuses={filterOptions.statuses}
        priorities={filterOptions.priorities}
        users={filterOptions.assignees}
        versions={[]}
        categories={[]}
        parentIssues={[]}
      />
    </div>
  )
}
